var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ModelLoader } from '../../loaders/ModelLoader';
import { ClTextureLoader } from '../../loaders/TextureLoader';
import { Texture } from 'three';
import { assertUnreachable } from '@canvas-logic/clp-utils';
import { TEXTURE_SCHEMA, convertTextureValue } from '../textures/TextureSchema';
export const SCENE_ENVIRONMENT_KEY = '__scene_environment__';
export class AssetsManager {
    constructor() {
        this.loadedAssets = new Map();
        this.missingAssets = [];
        this.modelLoader = new ModelLoader();
        this.textureLoader = new ClTextureLoader();
        this.defaultTexture = new Texture();
    }
    wantsAssets(assets) {
        for (const asset of assets) {
            if (this.loadedAssets.has(asset.key)) {
                continue;
            }
            this.missingAssets.push(asset);
        }
    }
    loadMissingAssets() {
        return __awaiter(this, void 0, void 0, function* () {
            yield Promise.all(this.missingAssets.map((asset) => this.loadAsset(asset)));
            this.missingAssets = [];
        });
    }
    getAsset(key) {
        var _a;
        return (_a = this.loadedAssets.get(key)) === null || _a === void 0 ? void 0 : _a.payload;
    }
    getModelAsset(key) {
        return this.getAsset(key);
    }
    registerAsset(key, asset, type = 'Texture') {
        this.loadedAssets.set(key, {
            prefetchAsset: { key, type, url: '' },
            payload: asset,
        });
    }
    loadAsset(asset) {
        return __awaiter(this, void 0, void 0, function* () {
            const type = asset.type;
            switch (type) {
                case 'Model3d': {
                    return this.loadModel3d(asset);
                }
                case 'Texture': {
                    return this.loadTexture(asset);
                }
                default:
                    assertUnreachable(type, `Unhandled asset type "${type}"`);
            }
        });
    }
    loadModel3d(asset) {
        return __awaiter(this, void 0, void 0, function* () {
            const model = yield this.modelLoader.load(asset.url);
            this.loadedAssets.set(asset.key, { prefetchAsset: asset, payload: model });
        });
    }
    loadTexture(asset) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            const prev = (_a = this.loadedAssets.get(asset.key)) === null || _a === void 0 ? void 0 : _a.prefetchAsset;
            const oldParams = (_b = prev === null || prev === void 0 ? void 0 : prev.params) !== null && _b !== void 0 ? _b : {};
            const texture = yield this.textureLoader.load(asset.url);
            if (!texture) {
                throw new Error(`Failed to load texture ${asset.url}`);
            }
            const params = (_c = asset.params) !== null && _c !== void 0 ? _c : {};
            const schema = TEXTURE_SCHEMA;
            for (const key of Object.keys(params)) {
                const spec = schema[key];
                const nextValue = params[key];
                if (!spec) {
                    texture[key] = nextValue;
                    texture.needsUpdate = true;
                    continue;
                }
                texture[key] = convertTextureValue(spec.kind, nextValue);
                if (spec.requiresNeedsUpdateOnChange)
                    texture.needsUpdate = true;
            }
            for (const key of Object.keys(oldParams)) {
                if (!(key in params)) {
                    this.resetTextureProperty(texture, key);
                    const spec = schema[key];
                    if (!spec || spec.requiresNeedsUpdateOnChange) {
                        texture.needsUpdate = true;
                    }
                }
            }
            this.loadedAssets.set(asset.key, { prefetchAsset: asset, payload: texture });
        });
    }
    resetTextureProperty(texture, key) {
        const defaultTex = this.defaultTexture;
        if (key in defaultTex) {
            texture[key] = defaultTex[key];
        }
        else {
            console.warn(`You are trying to reset unknown texture property ${key}. Setting it to undefined`);
            texture[key] = undefined;
        }
    }
}
//# sourceMappingURL=AssetsManager.js.map